/**
 * Message List - Displays messages in a channel
 */

import React, { useState, useEffect, useRef } from 'react';
import { List, Spin, Empty, message as antMessage, Modal, Input } from 'antd';
import { useQuery, useQueryClient } from 'react-query';
import api from '../../config/api';
import { useChatWebSocket } from '../../contexts/ChatWebSocketContext';
import { useAuth } from '../../contexts/AuthContext';
import { MessageItem } from './MessageItem';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import './MessageList.css';

dayjs.extend(relativeTime);

interface Message {
  id: string;
  channelId: string;
  userId: string;
  text: string;
  contentType: string;
  attachmentUrl?: string;
  attachmentName?: string;
  threadCount: number;
  createdAt: string;
  updatedAt?: string;
  isPinned?: boolean;
  isEdited?: boolean;
  isSystem?: boolean;
  systemType?: 'join' | 'leave' | 'invite' | 'rename';
  systemData?: {
    oldName?: string;
    newName?: string;
    userName?: string;
  };
  reactions?: Array<{
    emoji: string;
    count: number;
    userIds: string[];
    hasUserReaction?: boolean;
  }>;
  user: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
}

interface MessageListProps {
  channelId: string;
  onReplyToMessage?: (messageId: string) => void;
  onOpenThread?: (messageId: string) => void;
}

export const MessageList: React.FC<MessageListProps> = ({ channelId, onReplyToMessage, onOpenThread }) => {
  const { on: socketOn, presence } = useChatWebSocket();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messageListContainerRef = useRef<HTMLDivElement>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [editingMessageId, setEditingMessageId] = useState<string | null>(null);
  const [editText, setEditText] = useState('');
  
  // Expose scroll function via window for MessageInput to call
  useEffect(() => {
    (window as any).scrollChatToBottom = () => {
      const container = (window as any).chatMessageListContainer || document.querySelector('.message-list-container');
      if (container) {
        setTimeout(() => {
          container.scrollTop = container.scrollHeight;
        }, 100);
      }
    };
    return () => {
      delete (window as any).scrollChatToBottom;
    };
  }, []);

  // Load messages
  const { data: messagesData, refetch } = useQuery(
    ['chat-messages', channelId],
    () => api.get(`/api/chat/channels/${channelId}/messages`).then(res => res.data),
    {
      enabled: !!channelId,
      refetchOnWindowFocus: false,
    }
  );

  // Mark messages as read when channel is opened
  useEffect(() => {
    if (channelId) {
      api.post(`/api/chat/channels/${channelId}/read`).catch(err => {
        console.error('Error marking messages as read:', err);
      });
    }
  }, [channelId]);

  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [shouldScroll, setShouldScroll] = useState(false);

  useEffect(() => {
    if (messagesData?.messages) {
      // Fetch reactions for each message
      const fetchReactions = async () => {
        const messagesWithReactions = await Promise.all(
          messagesData.messages.map(async (msg: Message) => {
            try {
              const reactionsRes = await api.get(`/api/chat/channels/${channelId}/messages/${msg.id}/reactions`);
              return { ...msg, reactions: reactionsRes.data.reactions || [] };
            } catch {
              return { ...msg, reactions: [] };
            }
          })
        );
        setMessages(messagesWithReactions);
      };
      fetchReactions();
      // Don't scroll when switching channels - keep current scroll position
      setIsInitialLoad(false);
      setShouldScroll(false);
    }
  }, [messagesData, channelId]);

  // Reset flags when channel changes and scroll to bottom
  useEffect(() => {
    setIsInitialLoad(true);
    setShouldScroll(false);
    // Scroll to bottom when channel changes
    if (channelId && messagesData?.messages) {
      setTimeout(() => {
        const container = (window as any).chatMessageListContainer || document.querySelector('.message-list-container');
        if (container) {
          container.scrollTop = container.scrollHeight;
        }
      }, 300);
    }
  }, [channelId]);

  // Listen for new messages via WebSocket
  useEffect(() => {
    if (!channelId) return;

    const unsubscribe = socketOn('message:new', (newMessage: Message) => {
      if (newMessage.channelId === channelId) {
        setMessages(prev => {
          // Check if message already exists
          if (prev.some(m => m.id === newMessage.id)) return prev;
          return [...prev, newMessage];
        });
        // Always scroll for new messages (whether from WebSocket or user sending)
        setShouldScroll(true);
      }
    });

    return unsubscribe;
  }, [channelId, socketOn]);

  // Scroll to bottom only when shouldScroll is true (new message arrived)
  useEffect(() => {
    if (shouldScroll && messages.length > 0) {
      setTimeout(() => {
        const container = (window as any).chatMessageListContainer || document.querySelector('.message-list-container');
        if (container) {
          container.scrollTop = container.scrollHeight;
        }
        setShouldScroll(false);
      }, 150);
    }
  }, [shouldScroll, messages]);
  
  // Also scroll when messages array changes (for immediate feedback when user sends)
  useEffect(() => {
    if (messages.length > 0 && !isInitialLoad) {
      setTimeout(() => {
        const container = (window as any).chatMessageListContainer || document.querySelector('.message-list-container');
        if (container) {
          // Only scroll if user is near bottom (within 300px) or if it's a new message from current user
          const isNearBottom = container.scrollHeight - container.scrollTop - container.clientHeight < 300;
          if (isNearBottom) {
            container.scrollTop = container.scrollHeight;
          }
        }
      }, 100);
    }
  }, [messages.length, isInitialLoad]);

  const formatTime = (timestamp: string) => {
    return dayjs(timestamp).format('h:mm A');
  };

  const formatDateTime = (timestamp: string) => {
    const date = dayjs(timestamp);
    const now = dayjs();
    const isToday = date.isSame(now, 'day');
    const isYesterday = date.isSame(now.subtract(1, 'day'), 'day');
    
    if (isToday) {
      return `Today at ${date.format('h:mm A')}`;
    } else if (isYesterday) {
      return `Yesterday at ${date.format('h:mm A')}`;
    } else {
      return date.format('MMMM D, YYYY [at] h:mm A');
    }
  };

  const shouldShowDateSeparator = (current: Message, previous: Message | null) => {
    if (!previous) return true; // Always show for first message
    const currentDate = dayjs(current.createdAt);
    const previousDate = dayjs(previous.createdAt);
    // Show separator if date changed or more than 1 minute passed
    return !currentDate.isSame(previousDate, 'day') || 
           currentDate.diff(previousDate, 'minute') >= 1;
  };

  const isSameUser = (current: Message, previous: Message | null) => {
    if (!previous) return false;
    return current.userId === previous.userId &&
           dayjs(current.createdAt).diff(dayjs(previous.createdAt), 'minute') < 5;
  };

  if (!messagesData && channelId) {
    return (
      <div className="message-list-loading">
        <Spin size="large" />
      </div>
    );
  }

  if (messages.length === 0 && channelId) {
    return (
      <div className="message-list-empty">
        <Empty 
          description="No messages yet. Start the conversation!"
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        />
      </div>
    );
  }

  return (
    <div className="message-list">
      <List
        dataSource={messages}
        renderItem={(message, index) => {
          const previousMessage = index > 0 ? messages[index - 1] : null;
          const showAvatar = !isSameUser(message, previousMessage);
          const userPresence = presence[message.userId];
          const showDateSeparator = shouldShowDateSeparator(message, previousMessage);

          // Handle system messages (join/leave) as watermarks
          if (message.isSystem) {
            return (
              <>
                {showDateSeparator && (
                  <List.Item className="message-item date-separator" style={{ border: 'none', padding: '8px 16px' }}>
                    <div className="date-separator-watermark">
                      {formatDateTime(message.createdAt)}
                    </div>
                  </List.Item>
                )}
                <List.Item className="message-item system-message" style={{ border: 'none', padding: '8px 16px' }}>
                  <div className="system-message-watermark">
                    {message.systemType === 'join' && `👋 ${message.user.firstName} ${message.user.lastName} joined the channel`}
                    {message.systemType === 'leave' && `👋 ${message.user.firstName} ${message.user.lastName} left the channel`}
                    {message.systemType === 'invite' && `👋 ${message.user.firstName} ${message.user.lastName} was added to the channel`}
                    {message.systemType === 'rename' && (
                      message.systemData ? 
                        `✏️ ${message.systemData.userName || `${message.user.firstName} ${message.user.lastName}`} renamed the channel from "${message.systemData.oldName}" to "${message.systemData.newName}"` :
                        `✏️ ${message.user.firstName} ${message.user.lastName} renamed the channel`
                    )}
                  </div>
                </List.Item>
              </>
            );
          }

          const isOwnMessage = user?.id === message.userId;

          return (
            <>
              {showDateSeparator && (
                <List.Item className="message-item date-separator" style={{ border: 'none', padding: '8px 16px' }}>
                  <div className="date-separator-watermark">
                    {formatDateTime(message.createdAt)}
                  </div>
                </List.Item>
              )}
              <List.Item className={`message-item ${isOwnMessage ? 'own-message' : ''}`} style={{ border: 'none', padding: 0 }}>
                <MessageItem
                  message={message}
                  showAvatar={showAvatar}
                  userPresence={userPresence}
                  onReply={() => {
                    if (onReplyToMessage) {
                      onReplyToMessage(message.id);
                    }
                  }}
                  onOpenThread={(messageId) => {
                    if (onOpenThread) {
                      onOpenThread(messageId);
                    }
                  }}
                  onEdit={async (messageId: string, currentText: string) => {
                    // Check if message can be edited (within 10 minutes)
                    const message = messages.find(m => m.id === messageId);
                    if (message) {
                      const createdAt = new Date(message.createdAt);
                      const now = new Date();
                      const diffMinutes = (now.getTime() - createdAt.getTime()) / (1000 * 60);
                      if (diffMinutes > 10) {
                        antMessage.error('Messages can only be edited within 10 minutes of creation');
                        return;
                      }
                      setEditingMessageId(messageId);
                      setEditText(currentText);
                    }
                  }}
                  onReaction={async (messageId: string, emoji: string) => {
                    try {
                      await api.post(`/api/chat/channels/${channelId}/messages/${messageId}/reactions`, { emoji });
                      // Refetch reactions
                      const reactionsRes = await api.get(`/api/chat/channels/${channelId}/messages/${messageId}/reactions`);
                      setMessages(prev => prev.map(m => 
                        m.id === messageId 
                          ? { ...m, reactions: reactionsRes.data.reactions }
                          : m
                      ));
                    } catch (error: any) {
                      antMessage.error(error.response?.data?.error || 'Failed to update reaction');
                    }
                  }}
                  onDelete={() => {
                    // TODO: Implement delete
                    console.log('Delete message:', message.id);
                  }}
                  onPin={async () => {
                    try {
                      if (message.isPinned) {
                        await api.post(`/api/chat/channels/${channelId}/messages/${message.id}/unpin`);
                        antMessage.success('Message unpinned');
                      } else {
                        await api.post(`/api/chat/channels/${channelId}/messages/${message.id}/pin`);
                        antMessage.success('Message pinned');
                      }
                      queryClient.invalidateQueries(['pinned-messages', channelId]);
                      queryClient.invalidateQueries(['chat-messages', channelId]);
                    } catch (error: any) {
                      antMessage.error(error.response?.data?.error || 'Failed to pin/unpin message');
                    }
                  }}
                />
              </List.Item>
            </>
          );
        }}
      />
      <div ref={messagesEndRef} />
      
      {/* Edit Message Modal */}
      <Modal
        title="Edit Message"
        open={!!editingMessageId}
        onOk={async () => {
          if (!editText.trim()) {
            antMessage.error('Message cannot be empty');
            return;
          }
          try {
            await api.put(`/api/chat/channels/${channelId}/messages/${editingMessageId}`, { text: editText });
            antMessage.success('Message updated');
            setEditingMessageId(null);
            setEditText('');
            queryClient.invalidateQueries(['chat-messages', channelId]);
          } catch (error: any) {
            antMessage.error(error.response?.data?.error || 'Failed to edit message');
          }
        }}
        onCancel={() => {
          setEditingMessageId(null);
          setEditText('');
        }}
        okText="Save"
        cancelText="Cancel"
      >
        <Input.TextArea
          value={editText}
          onChange={(e) => setEditText(e.target.value)}
          autoSize={{ minRows: 3, maxRows: 10 }}
          onPressEnter={(e) => {
            if (e.ctrlKey || e.metaKey) {
              // Ctrl/Cmd + Enter to save
              e.preventDefault();
              // Trigger save
            }
          }}
        />
      </Modal>
    </div>
  );
};

